import { useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { clsx } from 'clsx';
import styles from './RankTrendChart.module.css';

export interface TrendSeries {
  key: string;
  label: string;
  points: { weekOf: string; rank: number | null }[];
}

const WIDTH = 480;
const HEIGHT = 220;
const MARGIN = { top: 14, right: 16, bottom: 28, left: 40 };
const INNER_W = WIDTH - MARGIN.left - MARGIN.right;
const INNER_H = HEIGHT - MARGIN.top - MARGIN.bottom;
const Y_TICKS = [1, 5, 10, 15];
const TIP_W = 210;

function weekLabel(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Where a practice ranked in each recent week, one line per surface. Rank 1 is the top of the
 * chart; the bottom edge is "not in the top N", so a miss reads as a drop off the list instead of
 * a made-up position. The values are also reachable as a table (the parent offers the toggle).
 */
export function RankTrendChart({ series, depth }: { series: TrendSeries[]; depth: number }) {
  const [active, setActive] = useState<number | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const weeks = useMemo(
    () =>
      [...new Set(series.flatMap((s) => s.points.map((p) => Date.parse(p.weekOf))))].sort(
        (a, b) => a - b,
      ),
    [series],
  );

  const x = (ms: number): number =>
    weeks.length < 2
      ? MARGIN.left + INNER_W / 2
      : MARGIN.left + ((ms - weeks[0]!) / (weeks[weeks.length - 1]! - weeks[0]!)) * INNER_W;
  // Rank 1 is the top edge; `depth + 1` (not found) is the bottom edge.
  const y = (rank: number | null): number =>
    MARGIN.top + (((rank ?? depth + 1) - 1) / depth) * INNER_H;

  const lineFor = (s: TrendSeries): string =>
    s.points
      .map((p) => ({ ms: Date.parse(p.weekOf), rank: p.rank }))
      .sort((a, b) => a.ms - b.ms)
      .map((p, i) => `${i ? 'L' : 'M'}${x(p.ms).toFixed(1)},${y(p.rank).toFixed(1)}`)
      .join(' ');

  const nearestWeek = (clientX: number): number | null => {
    const box = wrapRef.current?.getBoundingClientRect();
    if (!box || weeks.length === 0) return null;
    const svgX = ((clientX - box.left) / box.width) * WIDTH;
    return weeks.reduce((best, ms) =>
      Math.abs(x(ms) - svgX) < Math.abs(x(best) - svgX) ? ms : best,
    );
  };

  const onPointerMove = (event: PointerEvent) => setActive(nearestWeek(event.clientX));
  const onKeyDown = (event: KeyboardEvent) => {
    if (weeks.length === 0) return;
    const index = active === null ? weeks.length - 1 : weeks.indexOf(active);
    if (event.key === 'ArrowLeft') setActive(weeks[Math.max(0, index - 1)]!);
    else if (event.key === 'ArrowRight') setActive(weeks[Math.min(weeks.length - 1, index + 1)]!);
    else if (event.key === 'Escape') setActive(null);
    else return;
    event.preventDefault();
  };

  const rankAt = (s: TrendSeries, ms: number) => s.points.find((p) => Date.parse(p.weekOf) === ms);
  const valueText = (rank: number | null): string =>
    rank === null ? `Not in the top ${depth}` : `#${rank}`;

  return (
    <div className={styles.chart}>
      <div
        ref={wrapRef}
        className={styles.plot}
        tabIndex={0}
        role="group"
        aria-label="Rank by week. Use the left and right arrow keys to move between weeks."
        onPointerMove={onPointerMove}
        onPointerLeave={() => setActive(null)}
        onFocus={() => setActive((current) => current ?? weeks[weeks.length - 1] ?? null)}
        onBlur={() => setActive(null)}
        onKeyDown={onKeyDown}
      >
        <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className={styles.svg} aria-hidden="true">
          {Y_TICKS.map((tick) => (
            <g key={tick}>
              <line
                className={styles.grid}
                x1={MARGIN.left}
                x2={WIDTH - MARGIN.right}
                y1={y(tick)}
                y2={y(tick)}
              />
              <text
                className={styles.axis}
                x={MARGIN.left - 8}
                y={y(tick)}
                textAnchor="end"
                dominantBaseline="middle"
              >
                {tick}
              </text>
            </g>
          ))}
          <line
            className={styles.grid}
            x1={MARGIN.left}
            x2={WIDTH - MARGIN.right}
            y1={y(null)}
            y2={y(null)}
          />
          <text
            className={styles.axis}
            x={MARGIN.left - 8}
            y={y(null)}
            textAnchor="end"
            dominantBaseline="middle"
          >
            {depth}+
          </text>
          {weeks.map((ms, i) =>
            weeks.length > 6 && i % 2 === 1 ? null : (
              <text key={ms} className={styles.axis} x={x(ms)} y={HEIGHT - 8} textAnchor="middle">
                {weekLabel(ms)}
              </text>
            ),
          )}
          {active !== null && (
            <line
              className={styles.crosshair}
              x1={x(active)}
              x2={x(active)}
              y1={MARGIN.top}
              y2={MARGIN.top + INNER_H}
            />
          )}
          {series.map((s, index) => (
            <g key={s.key} className={styles[`series${index}`]}>
              {s.points.length > 1 && <path className={styles.line} d={lineFor(s)} />}
              {s.points.map((p) => (
                <g key={p.weekOf}>
                  <circle
                    className={styles.ring}
                    cx={x(Date.parse(p.weekOf))}
                    cy={y(p.rank)}
                    r={6}
                  />
                  <circle
                    className={styles.dot}
                    cx={x(Date.parse(p.weekOf))}
                    cy={y(p.rank)}
                    r={4}
                  />
                </g>
              ))}
            </g>
          ))}
          {active !== null && (
            <g
              className={styles.tip}
              transform={`translate(${
                x(active) > WIDTH * 0.6 ? x(active) - TIP_W - 10 : x(active) + 10
              },${MARGIN.top})`}
            >
              <rect
                className={styles.tipBox}
                width={TIP_W}
                height={26 + series.length * 18}
                rx={6}
              />
              <text className={styles.tipHead} x={10} y={17}>
                Week of {weekLabel(active)}
              </text>
              {series.map((s, index) => {
                const point = rankAt(s, active);
                const rowY = 36 + index * 18;
                return (
                  <g key={s.key}>
                    <line
                      className={clsx(styles.tipKey, styles[`keyStroke${index}`])}
                      x1={10}
                      x2={22}
                      y1={rowY - 4}
                      y2={rowY - 4}
                    />
                    <text className={styles.tipValue} x={28} y={rowY}>
                      {point ? valueText(point.rank) : '—'}
                    </text>
                    <text className={styles.tipLabel} x={TIP_W - 10} y={rowY} textAnchor="end">
                      {s.label}
                    </text>
                  </g>
                );
              })}
            </g>
          )}
        </svg>
        {active !== null && (
          <span className={styles.srOnly} role="status">
            {`Week of ${weekLabel(active)}. ${series
              .map((s) => {
                const point = rankAt(s, active);
                return `${s.label}: ${point ? valueText(point.rank) : 'not checked'}`;
              })
              .join('. ')}`}
          </span>
        )}
      </div>
      <ul className={styles.legend}>
        {series.map((s, index) => (
          <li key={s.key} className={styles.legendItem}>
            <span className={clsx(styles.key, styles[`keySeries${index}`])} />
            {s.label}
          </li>
        ))}
      </ul>
      <p className={styles.caption}>
        Higher is better (1 is first). {depth}+ means not in the top {depth}.
        {weeks.length < 2 && ' Only one week so far; the trend builds as weeks are checked.'}
      </p>
    </div>
  );
}
